import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { defer, from, map, Observable, switchMap } from 'rxjs';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { environment } from '@brighthub/environment';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AuthService } from '@brightside/core/auth';
import {
  Conversation,
  Message,
  MessageRole,
  Trace,
} from '../models/ask-navi-chat.models';
import { MessageService } from 'primeng/api';
import { ASK_NAVI_AGENT_CONFIG } from '../config/ask-navi-agent.config';
import { isAskNaviConversation } from '../utils/is-ask-navi-conversation';
import {
  processAgentCoreStream,
  type AgentCoreTraceResult,
  type DoneEvent,
} from '../utils/agentcore-stream';

/** List row shape from `GET /conversation/list` (may include `last_activity`). */
export interface ConversationListItem {
  id: string;
  title: string;
  created_at: string;
  last_activity?: string;
  updated_at?: string;
  user_id?: string;
  owner_id?: string;
  metadata?: Record<string, unknown>;
}

export interface ConversationListResponse {
  items: ConversationListItem[];
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasMore: boolean;
    limit: number;
  };
}

export interface StreamMessageUpdate {
  messageId: string;
  content: string;
  traces: Trace[];
  metadata?: Record<string, unknown>;
  finalized?: boolean;
}

/**
 * Standalone copilot HTTP client for Ask Navi. Replicates patterns from `CopilotApiService`
 * without injecting or calling it — no NgRx, no clientId, no cross-tab broadcast.
 */
@Injectable({
  providedIn: 'root',
})
export class AskNaviCopilotService {
  private readonly _http = inject(HttpClient);
  private readonly _authService = inject(AuthService);
  private readonly _messageService = inject(MessageService);

  private readonly _apiName = 'copilot';

  /**
   * Copilot API base URL from environment. Throws when missing so callers never build `undefined/...` paths.
   *
   * @remarks Use inside {@link defer} so the error is emitted on the Observable error channel.
   */
  private _requireCopilotBaseUrl(): string {
    const baseUrl = environment.apiEndpoints[this._apiName]?.trim();
    if (!baseUrl) {
      throw new Error(
        `[AskNaviCopilot] environment.apiEndpoints['${this._apiName}'] is not configured. ` +
          'Set the copilot API base URL in environment config.'
      );
    }
    return baseUrl;
  }

  private _getToken(): Observable<string | undefined> {
    return from(this._authService.getAccessToken());
  }

  private _getHttpHeaders(): Observable<HttpHeaders> {
    return this._getToken().pipe(
      map((token) => {
        if (!token) {
          this._messageService.add({
            severity: 'error',
            summary: 'Authentication Error',
            detail: 'You are not authenticated. Please log in again.',
          });
          throw new Error('No authentication token available');
        }
        return new HttpHeaders({ Authorization: `Bearer ${token}` });
      })
    );
  }

  private _getFetchHeaders(): Observable<Headers> {
    return this._getToken().pipe(
      map((token) => {
        if (!token) {
          this._messageService.add({
            severity: 'error',
            summary: 'Authentication Error',
            detail: 'You are not authenticated. Please log in again.',
          });
          throw new Error('No authentication token available');
        }
        return new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });
      })
    );
  }

  /**
   * Lists Ask Navi conversations for the authenticated user.
   * Client-side filter keeps only rows whose metadata matches {@link ASK_NAVI_AGENT_CONFIG}.
   *
   * @param page - Page number (1-based).
   * @param limit - Page size.
   */
  listConversations(page = 1, limit = 20): Observable<ConversationListItem[]> {
    return defer(() => {
      const url = `${this._requireCopilotBaseUrl()}/conversation/list?page=${page}&limit=${limit}&sort=desc`;
      return this._getHttpHeaders().pipe(
        switchMap((headers) =>
          this._http.get<ConversationListResponse>(url, { headers }).pipe(
            map((res) => (res.items ?? []).filter(isAskNaviConversation))
          )
        )
      );
    });
  }

  /**
   * Creates a new conversation via the copilot API.
   *
   * @param title - Optional initial title.
   */
  createConversation(title?: string): Observable<ConversationListItem> {
    return defer(() => {
      const url = `${this._requireCopilotBaseUrl()}/conversation/create`;
      return this._getHttpHeaders().pipe(
        switchMap((headers) =>
          this._http.post<ConversationListItem>(url, { title: title ?? undefined }, { headers })
        )
      );
    });
  }

  /**
   * Soft-deletes a conversation owned by the authenticated user.
   *
   * @param conversationId - Conversation to delete.
   * @returns void on HTTP 204 No Content.
   * @remarks Server sets `active_flag = false`; the row no longer appears in list responses.
   */
  deleteConversation(conversationId: string): Observable<void> {
    return defer(() => {
      const url = `${this._requireCopilotBaseUrl()}/conversation/${conversationId}`;
      return this._getHttpHeaders().pipe(
        switchMap((headers) =>
          this._http.delete(url, { headers, observe: 'response' }).pipe(map(() => undefined))
        )
      );
    });
  }

  /**
   * Loads message history for a conversation.
   *
   * @param conversationId - Conversation to load.
   */
  fetchMessages(conversationId: string): Observable<Message[]> {
    return defer(() => {
      const url = `${this._requireCopilotBaseUrl()}/conversation/${conversationId}/messages`;
      return this._getHttpHeaders().pipe(
        switchMap((headers) =>
          this._http.get<Conversation>(url, { headers }).pipe(
            map((res) =>
              (res.messages ?? []).map((message) => ({
                ...message,
                owner_id: res.owner_id,
              }))
            )
          )
        )
      );
    });
  }

  /**
   * Sends a user message and streams the assistant reply via NDJSON callbacks.
   *
   * @param conversationId - Target conversation.
   * @param content - User message text.
   * @param handlers - Streaming lifecycle callbacks.
   */
  sendMessageStream(
    conversationId: string,
    content: string,
    handlers: {
      onUserMessage?: (message: Message) => void;
      onAssistantStart?: (message: Message) => void;
      onAssistantUpdate?: (update: StreamMessageUpdate) => void;
      onComplete?: (finalMessage: Message) => void;
      onError?: (errorMessage: string) => void;
    }
  ): Observable<void> {
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: MessageRole.USER,
      content,
      created_at: new Date().toISOString(),
      conversation_id: conversationId,
      metadata: { finalized: true },
    };
    handlers.onUserMessage?.(userMessage);

    return defer(() => {
      const streamUrl = `${this._requireCopilotBaseUrl()}/conversation/messages/stream`;
      return this._getFetchHeaders().pipe(
        switchMap(
          (headers) =>
            new Observable<void>((observer) => {
              const controller = new AbortController();
              const timeout = setTimeout(() => controller.abort(), 120_000);

              fetch(streamUrl, {
              method: 'POST',
              headers,
              body: JSON.stringify({
                conversation_id: conversationId,
                content,
                namespace_id: ASK_NAVI_AGENT_CONFIG.namespaceId,
                agent_framework: ASK_NAVI_AGENT_CONFIG.agentFramework,
                agent_id: ASK_NAVI_AGENT_CONFIG.agentId,
                agent_alias_id: ASK_NAVI_AGENT_CONFIG.agentAliasId,
                agentcore_runtime_name: ASK_NAVI_AGENT_CONFIG.agentcoreRuntimeName,
              }),
              signal: controller.signal,
            })
              .then(async (response) => {
                clearTimeout(timeout);
                if (!response.ok || !response.body) {
                  throw new Error('Streaming response was not ok or body was missing.');
                }

                const reader = response.body.getReader();
                const assistantMessageId = `assistant-${Date.now()}`;
                let hasStarted = false;
                let contentSoFar = '';
                let traces: Trace[] = [];

                const emitUpdate = (metadata?: Record<string, unknown>) => {
                  handlers.onAssistantUpdate?.({
                    messageId: assistantMessageId,
                    content: contentSoFar,
                    traces,
                    metadata,
                  });
                };

                await processAgentCoreStream({
                  reader,
                  onChunk: (text: string) => {
                    contentSoFar += text;
                    if (!hasStarted) {
                      hasStarted = true;
                      handlers.onAssistantStart?.({
                        id: assistantMessageId,
                        role: MessageRole.ASSISTANT,
                        content: '',
                        created_at: new Date().toISOString(),
                        conversation_id: conversationId,
                        traces: [],
                        metadata: { finalized: false },
                      });
                    }
                    emitUpdate({ finalized: false, streamPaused: false });
                  },
                  onTrace: (trace: AgentCoreTraceResult) => {
                    traces = [
                      ...traces,
                      {
                        index: traces.length,
                        type: trace.type,
                        reasoning: trace.reasoning,
                        rationale: trace.rationale,
                        toolUsage: trace.toolUsage,
                      },
                    ];
                    if (hasStarted) {
                      emitUpdate({ finalized: false });
                    }
                  },
                  onComplete: (done?: DoneEvent) => {
                    const finalMessage: Message = {
                      id: done?.['id'] ?? assistantMessageId,
                      role: MessageRole.ASSISTANT,
                      content: contentSoFar,
                      created_at: done?.created_at ?? new Date().toISOString(),
                      conversation_id: conversationId,
                      traces,
                      metadata: {
                        finalized: true,
                        streamPaused: false,
                        ...(done ? { finalId: done['id'] } : {}),
                      },
                    };
                    handlers.onComplete?.(finalMessage);
                    observer.next();
                    observer.complete();
                  },
                  onError: (error: string) => {
                    handlers.onError?.(error);
                    observer.next();
                    observer.complete();
                  },
                });
              })
              .catch((err) => {
                clearTimeout(timeout);
                const isTimeout = err?.name === 'AbortError';
                const fallback = isTimeout
                  ? 'The assistant took too long to respond. Please try again.'
                  : 'Sorry, something went wrong while processing your message. Please try again.';
                handlers.onError?.(fallback);
                observer.next();
                observer.complete();
              });
            })
        )
      );
    });
  }
}
